import { useCallback, useRef, useState } from 'react';
import { loadGoogleMapsPlaces } from './useGoogleMapsLoader';

function componentText(components, type) {
  const match = components.find((c) => c.types.includes(type));
  return match ? match.longText : '';
}

function componentShortText(components, type) {
  const match = components.find((c) => c.types.includes(type));
  return match ? match.shortText : '';
}

function parseAddressComponents(components) {
  const streetNumber = componentText(components, 'street_number');
  const route = componentText(components, 'route');
  const city =
    componentText(components, 'locality') ||
    componentText(components, 'postal_town') ||
    componentText(components, 'sublocality');
  return {
    address: [streetNumber, route].filter(Boolean).join(' '),
    address2: componentText(components, 'subpremise'),
    city,
    state: componentShortText(components, 'administrative_area_level_1'),
    zip: componentText(components, 'postal_code'),
  };
}

/**
 * Wraps the Google Places Autocomplete Data API (places library "new"
 * classes: AutocompleteSuggestion + Place) so CheckoutPage can render its
 * own suggestion list instead of Google's built-in widget, matching the
 * rest of the checkout form's plain-input styling.
 */
export function useAddressAutocomplete() {
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const placesLibRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const requestIdRef = useRef(0);

  const ensurePlacesLib = useCallback(async () => {
    if (!placesLibRef.current) {
      placesLibRef.current = await loadGoogleMapsPlaces();
    }
    return placesLibRef.current;
  }, []);

  const fetchSuggestions = useCallback(async (input) => {
    const requestId = ++requestIdRef.current;
    if (!input || input.trim().length < 4) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    try {
      const places = await ensurePlacesLib();
      if (requestId !== requestIdRef.current) return; // a newer keystroke superseded this request
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new places.AutocompleteSessionToken();
      }
      const { suggestions: results } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: sessionTokenRef.current,
        includedRegionCodes: ['us'],
        includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
      });
      if (requestId !== requestIdRef.current) return;
      setSuggestions(results || []);
    } catch {
      if (requestId === requestIdRef.current) setSuggestions([]);
    } finally {
      if (requestId === requestIdRef.current) setIsSearching(false);
    }
  }, [ensurePlacesLib]);

  const clearSuggestions = useCallback(() => {
    requestIdRef.current += 1;
    setSuggestions([]);
    setIsSearching(false);
  }, []);

  const resolveSuggestion = useCallback(async (suggestion) => {
    const place = suggestion.placePrediction.toPlace();
    await place.fetchFields({ fields: ['addressComponents'] });
    sessionTokenRef.current = null; // a session ends once a place is selected
    clearSuggestions();
    return parseAddressComponents(place.addressComponents || []);
  }, [clearSuggestions]);

  return { suggestions, isSearching, fetchSuggestions, clearSuggestions, resolveSuggestion };
}
